// frontend/client/src/components/SavedContent.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Search, Eye, Clock, FolderOpen, Trash2,
  Instagram, Video as VideoIcon, MessageSquare,
  RefreshCw, Film, Sparkles, AlertCircle, ArrowRight,
  Brain, TrendingUp, Type
} from 'lucide-react';
import { useSelectedMedia } from '../context/SelectedMediaContext';
import { api } from '../services/apiClient';
import { useToast } from '../hooks/useToast';

const SC_CSS = `
  .sc-tab-btn { padding-bottom:12px;font-size:13px;font-weight:700;border:none;background:none;cursor:pointer;transition:all 0.15s;display:flex;align-items:center;gap:6px;font-family:var(--font-body,"Plus Jakarta Sans",sans-serif); }
  .sc-tab-btn.active { color:var(--brand-primary,#7C3AED);border-bottom:2px solid var(--brand-primary,#7C3AED); }
  .sc-tab-btn:not(.active) { color:oklch(0.65 0.03 285);border-bottom:2px solid transparent; }
  .sc-tab-btn:not(.active):hover { color:var(--text-main); }
  
  .sc-card { background:var(--bg-surface);border:1px solid var(--border-light);border-radius:16px;overflow:hidden;transition:all 0.2s;display:flex;flex-direction:column;box-shadow:0 1px 3px rgba(0,0,0,0.08); }
  .sc-card:hover { border-color:oklch(0.78 0.12 290);transform:translateY(-2px);box-shadow:0 6px 16px rgba(0,0,0,0.12); }
`;

function VideoCard({ video, onDelete, onEdit, onSendToFeed }) {
  const hasProcessed = !!video.processedUrl;
  
  const getThumbnail = () => {
    if (video.thumbnailUrl) return video.thumbnailUrl;
    const url = video.processedUrl || video.originalVideo?.secure_url || video.secure_url;
    if (url) {
      if (url.includes('cloudinary')) {
          return url.replace(/\.(mp4|mov|webm)$/i, '.jpg');
      }
      return url;
    }
    return null;
  };

  const thumbUrl = getThumbnail();

  return (
    <div className="sc-card">
      <div style={{ aspectRatio:'16/9',background:'oklch(0.15 0.04 285)',position:'relative',overflow:'hidden' }}>
        {thumbUrl && thumbUrl.endsWith('.jpg')
          ? <img src={thumbUrl} alt="thumbnail" style={{ width:'100%',height:'100%',objectFit:'cover',opacity:.85,transition:'opacity .2s' }} onError={e=>{e.target.style.display='none'}}/>
          : thumbUrl ? <video src={thumbUrl} preload="metadata" style={{ width:'100%',height:'100%',objectFit:'cover',opacity:.85 }} /> 
          : <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center' }}><Film size={36} style={{ color:'oklch(0.5 0.04 285)' }}/></div>
        }
        
        <div style={{ position:'absolute',bottom:10,right:10,display:'flex',gap:6 }}>
          <span style={{ background:'rgba(0,0,0,0.7)',color:'white',fontSize:10,fontWeight:700,padding:'4px 8px',borderRadius:6,fontFamily:'var(--font-body)' }}>
            {hasProcessed ? 'PROCESSED' : 'RAW'}
          </span>
          <span style={{ background:'rgba(0,0,0,0.7)',color:'white',fontSize:10,fontWeight:700,padding:'4px 8px',borderRadius:6,display:'flex',alignItems:'center',gap:4,fontFamily:'var(--font-body)' }}>
            <Clock size={10}/> {video.duration ? `${Math.round(video.duration)}s` : '--'}
          </span>
        </div>
      </div>
      <div style={{ padding:16,flex:1,display:'flex',flexDirection:'column' }}>
        <h4 style={{ margin:'0 0 4px',fontSize:14,fontWeight:700,color:'var(--text-main)',fontFamily:'var(--font-display)',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden' }}>
          {video.name || 'Untitled Video'}
        </h4>
        <div style={{ display:'flex',alignItems:'center',gap:6,color:'oklch(0.65 0.03 285)',fontSize:11,marginBottom:14,fontFamily:'var(--font-body)' }}>
          <FolderOpen size={12}/>
          <span>{new Date(video.createdAt).toLocaleDateString()}</span>
        </div>
        <div style={{ marginTop:'auto',display:'flex',gap:8 }}>
          <button
            onClick={() => onEdit(video)}
            style={{ flex:1,padding:'8px',borderRadius:8,background:'var(--bg-surface)',border:'1px solid var(--border-light)',color:'var(--text-main)',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'var(--font-body)' }}>
            Edit in Video Tools
          </button>
          <button
            onClick={() => onSendToFeed(video)}
            title="Send to Feed Preview"
            style={{ padding:'8px',borderRadius:8,background:'var(--bg-surface)',border:'1px solid var(--border-light)',color:'var(--brand-primary,#7C3AED)',cursor:'pointer' }}>
            <ArrowRight size={14}/>
          </button>
          <button onClick={() => onDelete(video._id, 'video')} style={{ padding:'8px',borderRadius:8,background:'#fee2e2',border:'1px solid #fecaca',color:'#dc2626',cursor:'pointer' }}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function CaptionCard({ caption, onDelete, onUse }) {
  return (
    <div className="sc-card">
      <div style={{ padding:16,borderBottom:'1px solid var(--border-light)' }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12 }}>
          <span style={{ display:'flex',alignItems:'center',gap:6,fontSize:11,fontWeight:800,color:'var(--brand-primary)',textTransform:'uppercase',letterSpacing:'0.05em',fontFamily:'var(--font-body)' }}>
            {caption.platform === 'instagram' ? <Instagram size={12}/> : <MessageSquare size={12}/>}
            {caption.platform}
          </span>
          <span style={{ fontSize:10,color:'oklch(0.65 0.03 285)',fontFamily:'var(--font-body)' }}>
            {new Date(caption.createdAt).toLocaleDateString()}
          </span>
        </div>
        <p style={{ fontSize:13,color:'var(--text-main)',lineHeight:1.6,margin:0,fontFamily:'var(--font-body)',whiteSpace:'pre-wrap',display:'-webkit-box',WebkitLineClamp:4,WebkitBoxOrient:'vertical',overflow:'hidden' }}>
          {caption.caption}
        </p>
      </div>
      <div style={{ padding:12,background:'var(--bg-surface)',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
        <div style={{ display:'flex',gap:6 }}>
          <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'oklch(0.94 0.04 285)',color:'var(--brand-primary)',fontFamily:'var(--font-body)' }}>{caption.tone}</span>
          <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'var(--bg-surface)',border:'1px solid var(--border-light)',color:'oklch(0.65 0.03 285)',fontFamily:'var(--font-body)' }}>{caption.length}</span>
        </div>
        <div style={{ display:'flex',gap:6 }}>
          <button onClick={() => onUse(caption)} style={{ padding:'6px 12px',borderRadius:6,background:'var(--brand-primary)',color:'var(--brand-primary-contrast, white)',border:'none',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'var(--font-body)' }}>Use</button>
          <button onClick={() => onDelete(caption._id, 'caption')} style={{ padding:'6px',borderRadius:6,background:'transparent',border:'1px solid var(--border-light)',color:'#dc2626',cursor:'pointer' }}><Trash2 size={12}/></button>
        </div>
      </div>
    </div>
  );
}

export default function SavedContent() {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const addToast = ({ message, type = 'success' }) => {
    if (showToast) showToast(message, type);
  };
  const navigate = useNavigate();
  const [tab, setTab] = useState('videos');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) return;
    const fetchSaved = async () => {
      setLoading(true);
      try {
        const endpoint = tab === 'videos' ? '/video/my-videos' : '/captions';
        const res = await api.get(endpoint);
        if (res.success) setItems(tab === 'videos' ? res.videos : (res.captions || []));
      } catch (err) {
        addToast({ type: 'error', message: `Failed to load ${tab}` });
      } finally {
        setLoading(false);
      }
    };
    fetchSaved();
  }, [currentUser, tab]);

  const handleDelete = async (id, type) => {
    try {
      const endpoint = type === 'video' ? `/video/${id}` : `/captions/${id}`;
      await api.delete(endpoint);
      setItems(prev => prev.filter(i => i._id !== id));
      addToast({ type: 'success', message: 'Item deleted' });
    } catch (err) {
      addToast({ type: 'error', message: 'Failed to delete item' });
    }
  };

  const handleEditInVideoTools = (video) => {
    navigate('/dashboard/video-tools', { state: { preloadedMedia: video } });
  };

  // ✅ FIXED: Pass caption and hashtags to FeedPreview
  const handleSendToFeedPreview = (video) => {
    navigate('/dashboard/preview', { 
      state: { 
        video: video,
        caption: video.caption || '',
        hashtags: video.hashtags || []
      } 
    });
  };

  const handleUseCaption = (caption) => {
    navigate('/dashboard/preview', {
      state: {
        caption: caption.caption,
        hashtags: caption.hashtags || [],
        platform: caption.platform,
      }
    });
  };

  // ✅ NEW: Temporary migration execution
  const handleMigrate = async () => {
    try {
      addToast({ type: 'info', message: 'Starting migration...' });
      const res = await api.post('/captions/migrate-from-videos');
      if (res.success) {
        addToast({ type: 'success', message: `Migrated ${res.details.captionsMigrated} captions!` });
        setTab('captions');
        window.location.reload(); 
      } else {
        addToast({ type: 'error', message: res.error || 'Migration failed.' });
      }
    } catch (err) {
      addToast({ type: 'error', message: 'Migration failed. Check console.' });
      console.error(err);
    }
  };

  const filteredItems = items.filter(item => {
    const s = search.toLowerCase();
    if (tab === 'videos') return item.name?.toLowerCase().includes(s);
    return item.caption?.toLowerCase().includes(s) || item.hashtags?.some(h => h.toLowerCase().includes(s));
  });

  return (
    <div style={{ padding:24,background:'var(--bg-canvas)',minHeight:'100%',display:'flex',flexDirection:'column' }}>
      <style>{SC_CSS}</style>

      {/* Header */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:16,marginBottom:24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontFamily:'var(--font-display)',fontSize:24,fontWeight:700,color:'var(--text-main)',margin:'0 0 6px',letterSpacing:'-0.02em' }}>
              My Posts & Media
            </h1>
            {/* TEMPORARY MIGRATION BUTTON */}
            <button 
              onClick={handleMigrate}
              style={{ padding: '6px 12px', background: 'var(--brand-primary)', color: 'var(--brand-primary-contrast, white)', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Sparkles size={12} style={{ marginRight: '4px' }}/>
              Recover Video Captions
            </button>
          </div>
          <p style={{ margin:0,fontSize:13,color:'oklch(0.65 0.03 285)',fontFamily:'var(--font-body)' }}>
            Your generated assets, ready for the compounding loop.
          </p>
        </div>
        <div style={{ position:'relative',width:240 }}>
          <Search size={14} color="oklch(0.65 0.03 285)" style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)' }}/>
          <input type="text" placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%',padding:'10px 12px 10px 34px',borderRadius:10,border:'1px solid var(--border-light)',background:'var(--bg-surface)',fontSize:13,fontFamily:'var(--font-body)',color:'var(--text-main)',outline:'none',boxSizing: 'border-box' }}/>
        </div>
      </div>

      {/* Tabs */}
      {/* ✅ FIXED: Clear state when switching tabs */}
      <div style={{ display:'flex',gap:24,borderBottom:'1px solid var(--border-light)',marginBottom:24 }}>
        <button 
          className={`sc-tab-btn ${tab==='videos'?'active':''}`} 
          onClick={() => {
            setItems([]);
            setTab('videos');
          }}>
          <VideoIcon size={14}/> Videos ({tab==='videos'?items.length:'...'})
        </button>
        <button 
          className={`sc-tab-btn ${tab==='captions'?'active':''}`} 
          onClick={() => {
            setItems([]);
            setTab('captions');
          }}>
          <Type size={14}/> Captions ({tab==='captions'?items.length:'...'})
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12 }}>
          <RefreshCw size={24} className="spin" color="var(--brand-primary)"/>
          <style>{`@keyframes spin { 100% { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
          <p style={{ fontSize:13,color:'oklch(0.65 0.03 285)',fontFamily:'var(--font-body)' }}>Loading your content engine...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && items.length === 0 && (
        <div style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',background:'var(--bg-surface)',borderRadius:20,border:'1px dashed var(--border-light)',padding:40 }}>
          <div style={{ width:48,height:48,borderRadius:12,background:'var(--bg-surface)',border:'1px solid var(--border-light)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:16 }}>
            {tab==='videos' ? <VideoIcon size={24} color="oklch(0.65 0.03 285)"/> : <MessageSquare size={24} color="oklch(0.65 0.03 285)"/>}
          </div>
          <h3 style={{ fontSize:16,fontWeight:700,color:'var(--text-main)',margin:'0 0 8px',fontFamily:'var(--font-display)' }}>
            {search ? 'No matches found' : `No ${tab==='videos'?'videos':'captions'} saved yet`}
          </h3>
          <p style={{ fontSize:13,color:'oklch(0.65 0.03 285)',maxWidth:360,margin:'0 auto 20px',lineHeight:1.6,fontFamily:'var(--font-body)' }}>
            {search?'Try a different search term.'
              :tab==='videos'?'Upload and process a video in Video Tools — it will appear here.'
              :"Generate captions in Content Lab — they'll appear here and feed into your brand loop."}
          </p>
          {!search && (
            <button 
              onClick={() => navigate(tab === 'videos' ? '/dashboard/video-tools' : '/dashboard/content-lab')}
              style={{ display:'inline-flex',alignItems:'center',gap:6,padding:'10px 20px',borderRadius:12,background:'linear-gradient(135deg,#7C3AED 0%,#3B82F6 100%)',color:'white',fontSize:13,fontWeight:700,border:'none',cursor:'pointer',fontFamily:'var(--font-body)' }}>
              {tab==='videos' ? 'Upload your first video' : 'Generate your first caption'} <ArrowRight size={13}/>
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {!loading && items.length > 0 && (
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:20 }}>
          {tab==='videos'
            ? filteredItems.map(item => <VideoCard key={item._id} video={item} onDelete={handleDelete} onEdit={handleEditInVideoTools} onSendToFeed={handleSendToFeedPreview} />)
            : filteredItems.map(item => <CaptionCard key={item._id} caption={item} onDelete={handleDelete} onUse={handleUseCaption} />)
          }
        </div>
      )}
    </div>
  );
}